/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button, Flex, Icon, SimpleGrid, Skeleton, useDisclosure, useToast } from '@chakra-ui/react'
import { GoogleMap, OverlayView, OverlayViewF } from '@react-google-maps/api'
import { Suspense, useCallback, useContext, useEffect } from 'react'
import { useQuery } from 'react-query'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { AuthContext } from '../../../App'
import { getVenueDetail } from '../../../api/user/play/Play'
import EventGalleryCard from '../../../components/cards/EventGalleryCard'
import { LocationMarkerDirection, MessageDots, SimpleLink } from '../../../components/icons/icons'
import LazyBox from '../../../components/lazy/LazyBox'
import CopyLinkModal from '../../../components/modals/CommunityCopyLinkModal'
import handleToast from '../../../components/toast/handleToast'
import useElementWidth from '../../../hooks/useElementWidth'
import useGoogleMaps from '../../../hooks/useGoogleMaps'
import useMobile from '../../../hooks/useMobile'
import { mapContainerStyle, mapStyles } from '../../../theme/mapStyles'

// Helpers re-exported from PlayExpand for legacy import sites (Datagrid, EventJoinedCard).
export function getGoogleMapsUrl(address: string) {
  const baseUrl = 'https://www.google.com/maps/'
  address = address.replace(' ', '+')
  address = address.replace(',', '')
  return `${baseUrl}?q=${address}`
}

export function getMapsUrl(address: string) {
  const formattedAddress = address.replace(/\s+/g, '+').replace(/,/g, '')
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
  if (isIOS) {
    const appleMapsUrl = `maps://maps.apple.com/?q=${formattedAddress}`
    const googleMapsUrl = `https://www.google.com/maps/?q=${formattedAddress}`
    const link = document.createElement('a')
    link.href = appleMapsUrl
    link.style.display = 'none'
    document.body.appendChild(link)
    setTimeout(() => {
      window.open(googleMapsUrl, '_blank')
    }, 500)
    link.click()
    setTimeout(() => {
      document.body.removeChild(link)
    }, 1000)
    return appleMapsUrl
  }
  return `https://www.google.com/maps/?q=${formattedAddress}`
}

const getPixelPositionOffset = (width: any) => ({
  x: -(width / 2),
  y: -98,
})

const MapCard = ({ venue }: { venue: any }) => {
  const { isLoaded } = useGoogleMaps()
  const zoom = 12
  const center = {
    lat: venue?.data?.location.lat ?? 0,
    lng: venue?.data?.location.lng ?? 0,
  }
  const onLoad = useCallback(function callback(map: any) {
    map.setZoom(12)
  }, [])
  const onUnmount = useCallback(function callback() {}, [])

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      onLoad={onLoad}
      zoom={zoom}
      onUnmount={onUnmount}
      options={{
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        zoomControl: false,
        styles: mapStyles,
        minZoom: zoom - 5,
      }}
    >
      {venue.isSuccess && (
        <OverlayViewF
          position={{
            lat: venue.data.location.lat,
            lng: venue.data.location.lng,
          }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={getPixelPositionOffset}
        >
          <Box
            w="90px"
            h="107px"
            bgImage="/components/map_pin.svg"
            bgPos="center"
            bgSize="contain"
            position="relative"
            onClick={() => window.open(getMapsUrl(venue?.data?.address))}
            cursor="pointer"
          >
            <LazyBox
              width={60}
              height={60}
              src={(venue?.data?.images?.length ?? 0 > 0) ? venue?.data?.images[0] : ''}
              top="15px"
              left="15px"
              borderRadius="30px"
            />
          </Box>
        </OverlayViewF>
      )}
    </GoogleMap>
  ) : (
    <Skeleton w="100%" h="100%" />
  )
}

const EventActions = ({ venue }: { venue: any }) => {
  const navigate = useNavigate()
  return (
    <SimpleGrid w="100%" gap="12px" minChildWidth="100px" mt="20px">
      <Button
        variant="heavy"
        size="md"
        onClick={() => window.open(getMapsUrl(venue?.data?.address))}
        leftIcon={
          <Icon w="21px" h="20px" viewBox="0 0 21 20" fill="none">
            <LocationMarkerDirection />
          </Icon>
        }
      >
        Directions
      </Button>
      <Button
        variant="outline"
        borderRadius="25px"
        size="md"
        leftIcon={
          <Icon w="20px" h="20px" viewBox="0 0 20 20" fill="none">
            <SimpleLink />
          </Icon>
        }
        onClick={() => window.open(venue?.data?.website)}
        isDisabled={!venue?.data?.website}
      >
        Web
      </Button>
      <Button
        variant="outline"
        borderRadius="25px"
        size="md"
        leftIcon={
          <Icon w="21px" h="20px" viewBox="0 0 21 20" fill="none">
            <MessageDots />
          </Icon>
        }
        onClick={() => navigate('/user/messages?to=staff')}
      >
        Contact
      </Button>
    </SimpleGrid>
  )
}

const DesktopMediaSection = ({ onShare, venue }: { onShare: () => void; venue: any }) => {
  const [thumbnailWidth, cardContainerRef] = useElementWidth<HTMLDivElement>()
  const isSmallDesktop = useMobile(1330)

  const getBoxWidth = () => {
    if (isSmallDesktop) return '400px'
    return '599px'
  }

  return (
    <Box w={getBoxWidth()} flexShrink={0} ref={cardContainerRef}>
      {venue.isFetching ? (
        <Skeleton h={`${Math.round(((thumbnailWidth ?? 0) * 3) / 2)}px`} w="100%" borderRadius="10px" />
      ) : (
        <EventGalleryCard
          thumbnailWidth={thumbnailWidth}
          h={`${Math.round(((thumbnailWidth ?? 0) * 3) / 2)}px`}
          thumbnails={venue?.data?.images ?? []}
          hourlyPrice={venue?.data?.shortest_event?.fee ?? 0}
          shortestDuration={venue?.data?.shortest_event?.duration ?? 0}
          hasBackButton
          hasShareButton
          onShare={onShare}
        />
      )}

      <EventActions venue={venue} />

      <Box w="100%" h="302px" mt="10px" borderRadius="10px" overflow="hidden">
        <MapCard venue={venue} />
      </Box>

      {venue.data?.video && venue.data?.video != '' && (
        <Box w="100%" h="302px" mt="20px" borderRadius="10px" overflow="hidden">
          <iframe
            width="100%"
            height="100%"
            src={venue.data?.video.replace('youtu.be', 'youtube.com/embed')}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </Box>
      )}
    </Box>
  )
}

// Context handed to nested venue routes via React Router's `<Outlet context={...} />`.
// Children (PlayExpand index, VenuePlayEvents, …) call `useOutletContext<VenueLayoutContext>()`
// to reach the shared venue query + share/play handlers, so each child doesn't refetch.
export type VenueLayoutContext = {
  id: string | undefined
  venue: ReturnType<typeof useQuery<any>>
  isDesktop: boolean
  onOpenPlay: (sport?: string) => void
  onShare: () => void
  onBack: () => void
}

// Parent route for `/user/play/:venueId`. On desktop (>=1110px) renders the persistent
// left media column + `<Outlet />` on the right so navigating to /events keeps the gallery
// mounted. On mobile renders only `<Outlet />` — children own the full screen.
// Eagerly trigger the lazy chunks for sibling tab pages the moment VenueLayout
// mounts, so they are cached in the browser before the user clicks a pill.
// This eliminates the white-screen Suspense flash on first tab switch.
const preloadTabs = () => {
  import('../VenueCourts')
  import('../VenueLessons')
  import('../VenueMemberships')
  import('./VenuePlayEvents')
  import('../PlayExpand')
}

// Skeleton shown by the inner Suspense while a tab chunk is still downloading
// (safety net for very fast clicks before preloading finishes).
const TabChunkFallback = () => (
  <Box px="15px" pt="15px">
    <Skeleton h="200px" borderRadius="10px" mb="12px" />
    <Skeleton h="60px" borderRadius="10px" mb="10px" />
    <Skeleton h="60px" borderRadius="10px" />
  </Box>
)

const VenueLayout = () => {
  const { venueId } = useParams<{ venueId: string }>()
  const isMobile = useMobile(600)
  const isDesktop = !useMobile(1110)
  const toast = useToast()
  const navigate = useNavigate()
  const { user, error } = useContext(AuthContext)

  // Kick off background chunk downloads for all sibling tabs.
  useEffect(() => { preloadTabs() }, [])

  const { isOpen: isCopyLinkOpen, onOpen: onOpenCopyLink, onClose: onCloseCopyLink } = useDisclosure()

  // 5-minute staleTime so navigating between hub / events / courts / lessons
  // (or briefly away and back) reuses the cached venue detail instead of
  // re-flashing the gallery skeleton. Children read this same query via
  // useOutletContext — they do not re-query getVenueDetail themselves.
  const venue = useQuery(['getVenueDetail', venueId], () => getVenueDetail({ id: venueId }), {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  })

  const onShare = () => {
    if (isMobile) {
      navigator.share({
        title: 'LVBL',
        text: 'Check out this location on LVBL',
        url: import.meta.env.VITE_LOCAL_ENDPOINT + `/#/user/play/${venueId}`,
      })
    } else onOpenCopyLink()
  }

  const onOpenPlay = (sport?: string) => {
    if (!user || error) {
      localStorage.setItem('redirectAfterLogin', `/user/play/${venueId}`)
      navigate('/auth/login')
      return
    }
    navigate(`/user/play/${venueId}/events${sport ? `?sport=${sport}` : ''}`)
  }

  const onBack = () => navigate('/user/play')

  const context: VenueLayoutContext = { id: venueId, venue, isDesktop, onOpenPlay, onShare, onBack }

  const copyLinkModal = (
    <CopyLinkModal
      link={`/#/user/play/${venueId}`}
      isOpen={isCopyLinkOpen}
      onClose={onCloseCopyLink}
      onCopyLink={() => {
        handleToast(toast, 'Link copied to clipboard!')
        onCloseCopyLink()
      }}
      caption="Check out this location on LVBL!"
    />
  )

  if (isDesktop) {
    return (
      <Flex p="30px" gap="30px" alignItems="flex-start">
        {copyLinkModal}
        <DesktopMediaSection venue={venue} onShare={onShare} />
        <Box flex="1" minW="0">
          <Suspense fallback={<TabChunkFallback />}>
            <Outlet context={context} />
          </Suspense>
        </Box>
      </Flex>
    )
  }

  return (
    <>
      {copyLinkModal}
      <Suspense fallback={<TabChunkFallback />}>
        <Outlet context={context} />
      </Suspense>
    </>
  )
}

export default VenueLayout
